import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api, apiError } from "../../lib/api";
import { useRealtime } from "../../lib/realtime";
import { ItemRow } from "./ItemRow";
import { RUN_STATUS, fmtDay, fmtTime, inputCls, runState, shiftLabel } from "./shared";

export function RunPanel({ runId, shifts, tz, canVerify, readOnly, onClose, onChanged }) {
  const [run, setRun] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/checklists/runs/${runId}`).then((r) => setRun(r.data)).catch((e) => toast.error(apiError(e))), [runId]);
  useEffect(() => { load(); }, [load]);
  useRealtime((msg) => { if (msg.type === "checklist.run" && msg.run?.id === runId) load(); });

  if (!run) return null;
  const st = RUN_STATUS[runState(run)];
  const locked = readOnly || run.status !== "open";
  const allDone = run.progress.done === run.progress.total;
  const sections = [];
  run.items.forEach((it) => {
    const key = it.section || "";
    let s = sections.find((x) => x.key === key);
    if (!s) { s = { key, items: [] }; sections.push(s); }
    s.items.push(it);
  });

  const onLogged = (data) => {
    setRun((r) => ({ ...r, progress: data.progress, items: r.items.map((it) => (it.id === data.item.id ? data.item : it)) }));
    onChanged?.();
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/checklists/runs/${runId}/submit`);
      toast.success("Checklist submitted for verification");
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const verify = async (approved) => {
    if (!approved && note.trim().length < 3) return toast.error("Add a note explaining what must be corrected");
    setBusy(true);
    try {
      await api.post(`/checklists/runs/${runId}/verify`, { approved, note });
      toast.success(approved ? "Checklist verified" : "Returned to the team for correction");
      setNote("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-end sm:items-center justify-center sm:p-6" onClick={onClose}>
      <div data-testid="run-panel" onClick={(e) => e.stopPropagation()} className="bg-[#F8FAFC] w-full sm:max-w-3xl max-h-[94vh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 sm:px-8 py-4 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400">{shiftLabel(shifts, run.shift)} shift · {fmtDay(run.operating_date)}</p>
            <h2 className="font-heading text-xl sm:text-2xl font-bold truncate mt-0.5" data-testid="run-panel-title">{run.template_name}</h2>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span data-testid="run-panel-status" className={`text-xs font-bold px-2.5 py-1 rounded-md border ${st.chip}`}>{st.label}</span>
              <span className="text-xs font-semibold text-slate-500">{run.progress.done}/{run.progress.total} lines · {run.progress.failed} exception{run.progress.failed === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button data-testid="run-panel-close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={22} /></button>
        </div>

        <div className="px-5 sm:px-8 py-6 space-y-6">
          {run.status === "open" && run.rejected_note && (
            <div data-testid="run-returned-note" className="border border-red-200 bg-red-50 rounded-xl px-4 py-3 text-sm">
              <p className="font-bold text-red-700 flex items-center gap-1"><ArrowCounterClockwise size={16} /> Returned by {run.rejected_by} · {fmtTime(run.rejected_at, tz)}</p>
              <p className="text-red-800 mt-1">{run.rejected_note}</p>
            </div>
          )}
          {run.status === "verified" && (
            <div data-testid="run-verified-note" className="border border-emerald-200 bg-emerald-50 rounded-xl px-4 py-3 text-sm">
              <p className="font-bold text-emerald-700 flex items-center gap-1"><ShieldCheck size={16} weight="fill" /> Verified by {run.verified_by} · {fmtTime(run.verified_at, tz)}</p>
              {run.verification_note && <p className="text-emerald-800 mt-1">{run.verification_note}</p>}
            </div>
          )}
          {run.status === "submitted" && (
            <p data-testid="run-submitted-note" className="text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              Submitted by {run.submitted_by} · {fmtTime(run.submitted_at, tz)} — awaiting manager verification. Lines are locked.
            </p>
          )}

          {sections.map((s) => (
            <div key={s.key || "_"} className="space-y-3">
              {s.key && <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 pt-2">{s.key}</p>}
              {s.items.map((it) => <ItemRow key={`${it.id}-${it.completed_at || ""}`} runId={runId} item={it} tz={tz} locked={locked} onLogged={onLogged} />)}
            </div>
          ))}

          {run.status === "open" && !readOnly && (
            <button data-testid="run-submit-button" onClick={submit} disabled={!allDone || busy}
              className="w-full h-14 rounded-xl bg-emerald-600 text-white font-heading font-bold text-base hover:bg-emerald-700 disabled:opacity-40 tap-press">
              {allDone ? (busy ? "Submitting…" : "Submit checklist for verification") : `${run.progress.total - run.progress.done} line(s) remaining`}
            </button>
          )}

          {run.status === "submitted" && canVerify && !readOnly && (
            <div data-testid="run-verify-block" className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
              <p className="font-heading font-bold">Manager verification</p>
              <textarea data-testid="run-verify-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Verification note (required when returning)" className={`${inputCls} h-auto py-2`} />
              <div className="flex gap-2">
                <button data-testid="run-approve-button" onClick={() => verify(true)} disabled={busy} className="h-12 flex-1 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 tap-press">Verify & sign off</button>
                <button data-testid="run-return-button" onClick={() => verify(false)} disabled={busy} className="h-12 flex-1 rounded-lg border border-red-300 text-red-700 text-sm font-bold bg-white hover:bg-red-50 disabled:opacity-40 tap-press">Return for correction</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
